import type {
  EntityKey, FetchPolicyValue, RequestFactoryContext, RequestHeaders, RequestVerb,
} from '../public-types';
import type { Schema, SchemaNodeLike } from '../schema';

export interface RequestContractOptions<
  TVariables extends object,
  TData,
  THeaders extends RequestHeaders | void = RequestHeaders | void,
  TPayload = unknown,
> {
  schema: Schema;
  dataPath: string;
  verb: RequestVerb;
  fetchPolicy?: FetchPolicyValue;
  uriFactoryFn: (_context: RequestFactoryContext<TVariables, TData>) => string;
  headerFactoryFn?: (_context: RequestFactoryContext<TVariables, TData>) => THeaders;
  payloadFactoryFn?: (_context: RequestFactoryContext<TVariables, TData>) => TPayload;
  keySelector?: (_variables: TVariables) => EntityKey;
  parentKeySelector?: (_variables: TVariables) => EntityKey;
}

export class RequestContract<
  TVariables extends object = object,
  TData = unknown,
  THeaders extends RequestHeaders | void = RequestHeaders | void,
  TPayload = unknown,
> {
  dataPath: string;
  fetchPolicy?: FetchPolicyValue;
  headerFactoryFn?: (_context: RequestFactoryContext<TVariables, TData>) => THeaders;
  keySelector?: (_variables: TVariables) => EntityKey;
  parentKeySelector?: (_variables: TVariables) => EntityKey;
  payloadFactoryFn?: (_context: RequestFactoryContext<TVariables, TData>) => TPayload;
  schema: Schema;
  uriFactoryFn: (_context: RequestFactoryContext<TVariables, TData>) => string;
  verb: RequestVerb;

  constructor(options: RequestContractOptions<TVariables, TData, THeaders, TPayload>) {
    Object.assign(this, options);
    this.getSchemaNode = this.getSchemaNode.bind(this);
  }

  getSchemaNode(): SchemaNodeLike {
    return this.schema.insert(this.dataPath);
  }
}

export function createRequestContract<
  TVariables extends object = object,
  TData = unknown,
  THeaders extends RequestHeaders | void = RequestHeaders | void,
  TPayload = unknown,
>(options: RequestContractOptions<TVariables, TData, THeaders, TPayload>) {
  if (!options.dataPath) {
    throw new Error('dataPath cannot be empty');
  }
  return new RequestContract(options);
}
